'use client'

import { PropsWithChildren, useRef } from 'react'
import Image from 'next/image'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { cn } from '@/lib/utils'
import BackgroundImage from '../../background-image'

interface ParallaxBackgroundEffectProps extends PropsWithChildren {
  className?: string
  src?: string
  parallaxPixels?: number
  imageClassName?: string
  /**
   * When true, the image is shown fully contained at its natural aspect ratio
   * on mobile (no cropping/zoom). The fixed-height, cover-cropped parallax
   * behavior still applies from the `sm` breakpoint up. The natural ratio is
   * resolved by the browser, so no intrinsic dimensions are required.
   */
  mobileContain?: boolean
}
gsap.registerPlugin(ScrollTrigger)

const ParallaxBackgroundEffect = ({
  children,
  className,
  src,
  parallaxPixels = 100,
  imageClassName,
  mobileContain,
}: ParallaxBackgroundEffectProps) => {
  const imageHeight = parallaxPixels * 2
  const scopeContainer = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!src) {
        return
      }
      gsap
        .timeline({
          defaults: {
            duration: 300,
          },
          scrollTrigger: {
            trigger: '.parallax-effect-parent',
            scrub: 1,
            start: 'top bottom',
            end: 'bottom top',
          },
        })
        .to('.parallax-effect-image', {
          transform: `translateY(-${parallaxPixels}px)`,
        })
    },
    {
      scope: scopeContainer,
    },
  )

  return (
    <div ref={scopeContainer}>
      {/* Mobile: show the full image contained at its natural aspect ratio so
          it is scaled down rather than cropped/zoomed. The natural ratio is
          resolved by the browser (width/height 0 + h-auto), so it works even
          when the source has no intrinsic dimensions. Opt-in only. */}
      {mobileContain && src && (
        <div className="relative w-full sm:hidden">
          <Image
            priority
            src={src}
            width={0}
            height={0}
            sizes="100vw"
            alt=""
            className="h-auto w-full object-contain"
          />
        </div>
      )}
      <div
        className={cn(
          'parallax-effect-parent relative overflow-hidden',
          // The fixed-height cover-cropped parallax is desktop-only when
          // mobileContain is set.
          mobileContain && 'max-sm:hidden',
          className,
        )}
      >
        <BackgroundImage
          src={src}
          classNameImg={cn('object-[0,38%]', imageClassName)}
          className="parallax-effect-image max-h-none"
          style={{ height: `calc(100% + ${imageHeight}px)` }}
        />

        {children}
      </div>
    </div>
  )
}

export default ParallaxBackgroundEffect
